/**
 * Student Management Types
 * 
 * Types for student profiles, attendance tracking, progress monitoring, and coach notes.
 */

import { BaseEntity } from './index';

/**
 * Skill rating for a specific tennis skill
 * Scale: 1-5 (1 = beginner, 5 = advanced)
 */
export type SkillRating = 1 | 2 | 3 | 4 | 5;

/**
 * Tennis skill categories for progress tracking
 */
export const TENNIS_SKILLS = [
  'forehand',
  'backhand',
  'serve',
  'volley',
  'footwork',
  'strategy',
  'mental_game',
] as const;

export type TennisSkill = typeof TENNIS_SKILLS[number];

/**
 * Student progress tracking
 * Tracks skill development, achievements, and assessment history
 */
export interface StudentProgress extends BaseEntity {
  studentId: string;
  academyId: string;
  skillLevel: 'beginner' | 'intermediate' | 'advanced';
  skillRatings: Partial<Record<TennisSkill, SkillRating>>;
  achievements: StudentAchievement[];
  lastAssessmentDate?: Date;
  lastAssessedBy?: string; // Coach ID
  notes?: string;
}

/**
 * Achievement earned by a student
 */
export interface StudentAchievement {
  id: string;
  title: string;
  description?: string;
  earnedAt: Date;
  awardedBy: string; // Coach ID
  category: 'skill' | 'participation' | 'tournament' | 'improvement' | 'other';
}

/**
 * Student attendance summary for a booking
 * Tracks attendance at the booking/enrollment level
 */
export interface StudentAttendance extends BaseEntity {
  studentId: string;
  bookingId: string;
  lessonId: string;
  academyId: string;
  lessonsTotal: number;
  lessonsAttended: number;
  lessonsMissed: number;
  lessonsCancelled: number;
  attendanceRate: number; // Percentage 0-100
  lastAttendedDate?: Date;
  streak?: number; // Current consecutive attendance streak
  /** Present when each row reflects one attendance record / session */
  lessonDate?: Date;
  sessionStatus?: 'scheduled' | 'attended' | 'missed' | 'cancelled';
  notes?: string;
  /** Lesson list price for this session row (when the API loaded lesson pricing). */
  lessonPrice?: number;
}

/** One flattened session row for CSV / spreadsheet export (academy-wide or per-student). */
export interface AttendanceHistoryExportRow {
  studentId: string;
  firstName: string;
  lastName: string;
  lessonId: string;
  lessonName: string;
  lessonType: string;
  /** Amount for this session (camp day, private per-session, or term/list price as applicable). */
  lessonPrice: number;
  lessonDate: string;
  status: string;
  notes: string;
}

/** Aggregated billing row per student per lesson (owner payments report). */
export interface StudentBillingReportRow {
  studentId: string;
  firstName: string;
  lastName: string;
  lessonId: string;
  lessonName: string;
  lessonType: 'group' | 'private' | 'camp';
  termId?: string | null;
  termName?: string;
  bookingId: string;
  bookingStatus: string;
  paymentStatus: string;
  /** Camp bookings: daily per-day rows vs full camp package */
  campBookingType?: 'daily' | 'full_camp';
  sessionsTotal: number;
  /** Full enrollment attendance rows, unaffected by period filters. */
  enrollmentSessionsTotal?: number;
  sessionsAttended: number;
  /** Attended session dates (YYYY-MM-DD) in the selected period, when applicable. */
  attendedDates?: string[];
  sessionsMissed: number;
  sessionsCancelled: number;
  /** List price for one session when applicable */
  perSessionPrice: number;
  /** Full term or package list price */
  fullPrice: number;
  /** Per-session price set on the lesson (group lessons with per-session pricing). */
  sessionPrice?: number | null;
  /** Selected reporting period (YYYY-MM-DD), when a period filter is applied. */
  periodStart?: string;
  periodEnd?: string;
  /** Amount due for the selected period; falls back to `totalDue` when no period is selected. */
  periodTotalDue?: number | null;
  billingBasis?:
    | 'enrollment_total'
    | 'attendance_in_period'
    | 'term_full_price_due_in_period'
    | 'term_full_price_outside_period'
    | 'missing_session_price';
  totalDue: number;
}

/** Session-level drill-down for student billing report. */
export interface StudentBillingSessionRow {
  sessionDate: string;
  status: string;
  price: number;
  notes?: string;
}

/** Report-wide totals across all matching enrollments (not just the current page). */
export interface StudentBillingReportSummary {
  totalDue: number;
  sessionsAttended: number;
  sessionsTotal: number;
  /** Enrollments that cannot be billed by attendance (missing per-session price). */
  missingSessionPriceCount?: number;
}

export interface StudentBillingReportPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export const STUDENT_BILLING_REPORT_DEFAULT_PAGE_SIZE = 20;
export const STUDENT_BILLING_REPORT_MAX_PAGE_SIZE = 100;

export interface StudentBillingReportResult {
  aggregates: StudentBillingReportRow[];
  detailed?: StudentBillingSessionRow[];
  summary?: StudentBillingReportSummary;
  pagination?: StudentBillingReportPagination;
}

/**
 * Coach note on a student
 * Used for communication and progress documentation
 */
export interface CoachNote extends BaseEntity {
  studentId: string;
  academyId: string;
  coachId: string;
  coachName: string;
  content: string;
  category: 'general' | 'progress' | 'behavior' | 'goal' | 'parent_communication';
  isPrivate: boolean; // If true, only visible to coaches, not parents
  readByParent?: boolean;
  readAt?: Date;
  /** Pathway skill assessment (category should be `progress`) */
  programId?: string;
  programSkillName?: string;
  rating?: SkillRating;
  skillAchieved?: boolean;
}

/** Append-only history row: pathway note assessments + sport progress snapshots */
export type StudentAssessmentEventSource =
  | 'program_skill_note'
  | 'tennis_progress_update'
  | 'sport_progress_update';

export interface StudentAssessmentEvent {
  id: string;
  studentId: string;
  academyId: string;
  coachId: string;
  coachName: string;
  createdAt: Date;
  source: StudentAssessmentEventSource;
  coachNoteId?: string;
  programId?: string;
  programSkillName?: string;
  rating?: SkillRating;
  skillAchieved?: boolean;
  tennisSkillRatings?: Partial<Record<TennisSkill, SkillRating>>;
  skillLevel?: string;
  narrative: string;
}

/**
 * Extended student profile with management data
 * Combines base Student with progress, attendance, and notes summary
 */
export interface StudentProfile {
  id: string;
  firstName: string;
  lastName: string;
  /** May be absent for staff-created records until captured. */
  dateOfBirth?: Date | null;
  gender: 'male' | 'female' | 'other';
  skillLevel: 'beginner' | 'intermediate' | 'advanced';
  medicalNotes?: string;
  emergencyContact?: {
    name: string;
    phone: string;
    relationship: string;
    email?: string;
  };
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
  academyId?: string;
  /** Guardian user id when linked; absent for manual students */
  parentId?: string;
  /** True when this student record represents the account holder (self-student). */
  isSelf?: boolean;
  /** Email from the guardian’s academy profile (when parent is linked). */
  guardianEmail?: string;
  /** E.164 mobile from the guardian’s academy profile (when parent is linked). */
  guardianPhone?: string;
  /** Guardian SMS opt-in for this academy (`academy_profiles.sms_consent`). */
  guardianSmsConsent?: boolean;
  isManual?: boolean;
  inviteEmail?: string;
  invitePhone?: string;
  // Extended management data
  attendanceSummary?: {
    totalLessons: number;
    attendedLessons: number;
    attendanceRate: number;
  };
  progressSummary?: {
    overallLevel: 'beginner' | 'intermediate' | 'advanced';
    recentAchievements: string[];
    lastAssessmentDate?: Date;
  };
  recentNotes?: CoachNote[];
  activeBookings?: number;
  /**
   * Latest activity (YYYY-MM-DD) for owner list Status: max of last **attended** session
   * and latest **booking** created/updated time (pending/confirmed/completed) in this academy.
   * Active when within STUDENT_LIST_RECENCY_ACTIVE_WITHIN_DAYS (90 days).
   */
  listRecencyDate?: string;
  /** Owner student list: primary lesson + program / age label for table columns */
  enrollmentListMeta?: {
    primaryLessonName?: string;
    programName?: string;
    ageOrPathwayLabel?: string;
    /** Calendar swatch colour from linked age group. */
    calendarColor?: string;
  };
}

/** Other students in the same academy sharing the same guardian (parent) */
export interface StudentSiblingSummary {
  id: string;
  firstName: string;
  lastName: string;
  currentLessonNames: string[];
}

/**
 * Filters for student list queries
 */
export interface StudentFilters {
  skillLevel?: 'beginner' | 'intermediate' | 'advanced';
  /** Soft-delete flag on the student row (not the owner list Active/Inactive badge). */
  isActive?: boolean;
  /** Owner list status: recent attendance/booking activity within {@link STUDENT_LIST_RECENCY_ACTIVE_WITHIN_DAYS}. */
  listRecencyActive?: boolean;
  ageMin?: number;
  ageMax?: number;
  searchQuery?: string;
  sortBy?: 'name' | 'skillLevel' | 'attendanceRate' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
  /** Filter by academy age group (custom grouping) */
  ageGroupId?: string;
  /** Legacy / Hot Shots ball colour when no custom age group */
  ballColor?: 'blue' | 'red' | 'orange' | 'green' | 'yellow';
  /** Program (pathway) — student must have an active booking on a lesson in this program */
  programId?: string;
  /** Restrict listing to these student IDs (must still be enrolled at the academy). */
  studentIds?: string[];
  /** Students enrolled on this lesson session occurrence (roster semantics). */
  sessionId?: string;
}









